import React from "react";
import {
    Button,
    Col,
    Form,
    Row,
    ToggleButton,
    ToggleButtonGroup,
} from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";

import { alertActions } from "../../features/alert/alertSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { updateSettings } from "../../lib/api";
import { ISiteSettingsForm } from "../../types/forms";

const DisplaySettings = (): React.JSX.Element | undefined => {
    const dispatch = useAppDispatch();

    const settings = useAppSelector(state => state.global.settings);

    const settingsToShow = [
        "majorybl_show_games_on_umphub",
        "majorybl_display_resume_link",
        "majorybl_display_placement_link",
        "majorybl_display_delete_game_link_for_coaches",
        "majorybl_display_create_game_link_for_coaches",
        "majorybl_hs_display_create_game_link_for_coaches",
        "majorybl_hs_display_delete_game_link_for_coaches",
        "majorybl_display_score_entry",
    ];

    const {
        register,
        setValue,
        watch,
        getValues,
        formState,
        handleSubmit,
        reset,
    } = useForm<Partial<ISiteSettingsForm>>({
        mode: "onChange",
        defaultValues: {
            display: settingsToShow.reduce(
                (acc, settingName) => {
                    const setting = settings.find(
                        s => s.settingName === settingName,
                    );
                    if (setting) {
                        acc[settingName] = setting.setting;
                    }
                    return acc;
                },
                {} as Record<string, any>,
            ),
        },
    });

    const onSubmit: SubmitHandler<Partial<ISiteSettingsForm>> = async data => {
        const dirtyFields = formState.dirtyFields;

        const filteredData: Partial<ISiteSettingsForm> = {
            display: {},
        };

        (Object.keys(dirtyFields) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                const dirtySubfields = dirtyFields[category];
                Object.keys(dirtySubfields).forEach(subfield => {
                    if (data[category]?.hasOwnProperty(subfield)) {
                        filteredData[category][subfield] =
                            data[category][subfield];
                    }
                });
            },
        );

        (Object.keys(filteredData) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                if (Object.keys(filteredData[category]).length === 0) {
                    delete filteredData[category];
                }
            },
        );

        await updateSettings(filteredData)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Settings updated successfully",
                        dismissible: true,
                    }),
                );

                reset(data);
            })
            .catch(e =>
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating the settings.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                ),
            );
    };

    return (
        <section className="displaySettingsContent">
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                    {settings
                        .filter(s => settingsToShow.includes(s.settingName))
                        .map((s, idx) => {
                            const watchField = watch(
                                `display.${s.settingName}`,
                                getValues(`display.${s.settingName}`),
                            );

                            return (
                                <Form.Group
                                    controlId={s.settingName}
                                    as={Col}
                                    xs={6}
                                    key={idx}
                                    className="displaySetting"
                                >
                                    <Form.Label>{s.displayTitle}</Form.Label>
                                    <br />
                                    <Form.Control
                                        type="hidden"
                                        {...register(
                                            `display.${s.settingName}`,
                                            {
                                                value: watchField
                                                    ? "true"
                                                    : getValues(
                                                          `display.${s.settingName}`,
                                                      ) || s.setting.toString(),
                                            },
                                        )}
                                    />
                                    <ToggleButtonGroup
                                        type="radio"
                                        name={`display.${s.settingName}`}
                                        onChange={value => {
                                            setValue(
                                                `display.${s.settingName}`,
                                                value.toString(),
                                                {
                                                    shouldDirty: true,
                                                },
                                            );
                                        }}
                                    >
                                        <ToggleButton
                                            id={`display.${s.settingName}_on`}
                                            value="true"
                                            variant={
                                                watchField === "true"
                                                    ? "primary"
                                                    : "outline-primary"
                                            }
                                        >
                                            Yes
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`display.${s.settingName}_off`}
                                            value="false"
                                            variant={
                                                watchField === "false"
                                                    ? "primary"
                                                    : "outline-primary"
                                            }
                                        >
                                            No
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Form.Group>
                            );
                        })}
                </Row>
                <Button variant="primary" type="submit">
                    Update Settings
                </Button>
            </Form>
        </section>
    );
};

export default DisplaySettings;
